import { debugLog } from '../../../utils/logger';

/**
 * MLDebugger - Utility per verificare il funzionamento dell'analisi ML
 * Fornisce funzioni di diagnostica e logging per il machine learning
 */
export class MLDebugger {
  /**
   * Verifica se le funzionalità ML sono attive e utilizzabili
   * @param {Object} userData - Dati dell'utente (workouts e sets)
   * @returns {Object} Stato di attivazione del ML e dettagli
   */
  static verifyMLStatus(userData) {
    //debugLog('🔍 Verificando stato ML...');
    
    const { workouts = [], sets = [] } = userData || {};
    
    const hasMinimumData = workouts.length >= 10 && sets.length >= 50;
    const hasOptimalData = workouts.length >= 20 && sets.length >= 100;
    
    const status = {
      isActive: hasMinimumData,
      dataQuality: hasOptimalData ? 'optimal' : (hasMinimumData ? 'sufficient' : 'insufficient'),
      workoutsCount: workouts.length,
      setsCount: sets.length,
      requiredWorkouts: 9,
      requiredSets: 50,
      features: {
        recoveryPredictor: hasMinimumData,
        plateauDetection: hasMinimumData && workouts.length >= 15,
        patternRecognition: hasMinimumData,
        volumeOptimizer: hasOptimalData
      }
    };
    
    //debugLog('📊 Stato ML:', status);
    
    return status;
  }
  
  /**
   * Monitora il processo di inizializzazione ML
   * @param {Function} initFunction - Funzione di inizializzazione ML da monitorare
   * @param {Object} userData - Dati dell'utente
   * @param {Object} userProfile - Profilo dell'utente
   * @returns {Promise} Risultato dell'inizializzazione
   */
  static async monitorMLInitialization(initFunction, userData, userProfile = {}) {
    //debugLog('🚀 Inizializzazione ML in corso...');
    const startTime = performance.now();
    
    try {
      const result = await initFunction(userData.workouts, userData.sets, userProfile);
      
      const endTime = performance.now();
      const duration = (endTime - startTime).toFixed(2);
      
      //debugLog(`✅ Inizializzazione ML completata in ${duration}ms`);
      
      if (result) {
        //debugLog('🧠 Modelli ML inizializzati:', Object.keys(result.trainedModels || {}));
        //debugLog('👤 Metriche utente:', result.userMetrics);
        //debugLog('🔄 Pattern rilevati:', result.workoutPatterns.length);
      } else {
        //debugLog('⚠️ Inizializzazione ML completata ma senza risultati');
      }
      
      return result;
    } catch (error) {
      //debugLog('❌ Errore durante l\'inizializzazione ML:', error);
      throw error;
    }
  }
  
  /**
   * Esegue test di funzionalità ML e registra i risultati
   * @param {Object} mlAnalyzer - Istanza di MLAnalyzer
   * @returns {Object} Risultati dei test
   */
  static runMLFeatureTests(mlAnalyzer) {
    if (!mlAnalyzer) {
      //debugLog('❌ Impossibile eseguire test ML: analyzer non disponibile');
      return { success: false, error: 'ML Analyzer non disponibile' };
    }
    
    //debugLog('🧪 Esecuzione test funzionalità ML...');
    
    const testResults = {
      patternDetection: this.testPatternDetection(mlAnalyzer),
      recoveryPrediction: this.testRecoveryPrediction(mlAnalyzer),
      periodizationSuggestion: this.testPeriodizationSuggestion(mlAnalyzer),
      personalizedSuggestions: this.testPersonalizedSuggestions(mlAnalyzer)
    };
    
    const successCount = Object.values(testResults).filter(result => result.success).length;
    const totalTests = Object.keys(testResults).length;
    
    //debugLog(`✅ Test ML completati: ${successCount}/${totalTests} successi`);
    
    return {
      success: successCount > 0,
      testsRun: totalTests,
      successfulTests: successCount,
      results: testResults
    };
  }
  
  /**
   * Testa il rilevamento di pattern
   * @param {Object} mlAnalyzer - Istanza di MLAnalyzer
   * @returns {Object} Risultato del test
   */
  static testPatternDetection(mlAnalyzer) {
    try {
      const patterns = mlAnalyzer.workoutPatterns;
      const success = Array.isArray(patterns) && patterns.length > 0;
      
      //debugLog(`${success ? '✅' : '❌'} Test rilevamento pattern: ${success ? 'OK' : 'Fallito'}`);
      if (success) {
        //debugLog('📊 Pattern rilevati:', patterns.length);
        patterns.forEach((p, i) => {
          //debugLog(`  Pattern ${i+1}: ${p.type} - ${p.pattern || ''} (confidenza: ${p.confidence})`);
        });
      }
      
      return { success, patterns };
    } catch (error) {
      //debugLog('❌ Errore nel test rilevamento pattern:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Testa la predizione del recupero
   * @param {Object} mlAnalyzer - Istanza di MLAnalyzer
   * @returns {Object} Risultato del test
   */
  static testRecoveryPrediction(mlAnalyzer) {
    try {
      // Testa la predizione per gruppi muscolari comuni
      const muscleGroups = ['Petto', 'Schiena', 'Gambe', 'Spalle'];
      const predictions = muscleGroups.map(group => ({
        group,
        prediction: mlAnalyzer.predictRecoveryTime(group)
      }));
      
      const success = predictions.some(p => p.prediction && p.prediction.estimatedDays > 0);
      
      //debugLog(`${success ? '✅' : '❌'} Test predizione recupero: ${success ? 'OK' : 'Fallito'}`);
      if (success) {
        predictions.forEach(p => {
          if (p.prediction) {
            //debugLog(`  ${p.group}: ${p.prediction.estimatedDays} giorni (confidenza: ${p.prediction.confidence})`);
          }
        });
      }
      
      return { success, predictions };
    } catch (error) {
      //debugLog('❌ Errore nel test predizione recupero:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Testa il suggerimento di periodizzazione
   * @param {Object} mlAnalyzer - Istanza di MLAnalyzer
   * @returns {Object} Risultato del test
   */
  static testPeriodizationSuggestion(mlAnalyzer) {
    try {
      const suggestion = mlAnalyzer.suggestPeriodizationModel();
      const success = suggestion && suggestion.recommendedModel;
      
      //debugLog(`${success ? '✅' : '❌'} Test suggerimento periodizzazione: ${success ? 'OK' : 'Fallito'}`);
      if (success) {
        //debugLog(`  Modello: ${suggestion.recommendedModel} (confidenza: ${suggestion.confidence})`);
        //debugLog(`  Spiegazione: ${suggestion.explanation}`);
      }
      
      return { success, suggestion };
    } catch (error) {
      //debugLog('❌ Errore nel test suggerimento periodizzazione:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Testa la generazione di suggerimenti personalizzati
   * @param {Object} mlAnalyzer - Istanza di MLAnalyzer
   * @returns {Object} Risultato del test
   */
  static testPersonalizedSuggestions(mlAnalyzer) {
    try {
      const suggestions = mlAnalyzer.generatePersonalizedSuggestions();
      const success = Array.isArray(suggestions) && suggestions.length > 0;
      
      //debugLog(`${success ? '✅' : '❌'} Test suggerimenti personalizzati: ${success ? 'OK' : 'Fallito'}`);
      if (success) {
        //debugLog(`  Generati ${suggestions.length} suggerimenti`);
        suggestions.forEach((s, i) => {
          //debugLog(`  Suggerimento ${i+1}: ${s.title} (tipo: ${s.type}, priorità: ${s.priority})`);
        });
      }
      
      return { success, count: suggestions.length };
    } catch (error) {
      //debugLog('❌ Errore nel test suggerimenti personalizzati:', error);
      return { success: false, error: error.message };
    }
  }
  
  /**
   * Monitora i risultati dell'analisi ML integrata in TrainingAnalyzer
   * @param {Function} analyzeFunction - Funzione di analisi da monitorare
   * @param {Object} data - Dati per l'analisi
   * @returns {Promise} Risultati dell'analisi
   */
  static async monitorTrainingAnalysis(analyzeFunction, data) {
    //debugLog('🔍 Analisi con ML integrata in corso...');
    const startTime = performance.now();
    
    try {
      const results = await analyzeFunction(data);
      
      const endTime = performance.now();
      const duration = (endTime - startTime).toFixed(2);
      
      //debugLog(`✅ Analisi completata in ${duration}ms`);
      
      if (results.mlAnalysis) {
        const { dataQuality, confidenceLevel, suggestions } = results.mlAnalysis;
        //debugLog(`📊 Qualità dati ML: ${dataQuality}`);
        //debugLog(`🎯 Livello confidenza: ${confidenceLevel}`);
        //debugLog(`💡 Suggerimenti ML: ${suggestions.length}`);
      } else {
        //debugLog('⚠️ Analisi completata senza componente ML');
      }
      
      return results;
    } catch (error) {
      //debugLog('❌ Errore durante l\'analisi:', error);
      throw error;
    }
  }
}

/**
 * Componente di diagnostica integrato per l'analisi ML
 * Aggiungilo ai componenti React che utilizzano ML
 */
export const MLDiagnosticsHook = ({ workouts, sets, userProfile }) => {
  //debugLog('🔍 ML Diagnostics Hook inizializzato');
  
  // Esegui verifica solo in fase di sviluppo
  if (process.env.NODE_ENV === 'development') {
    const mlStatus = MLDebugger.verifyMLStatus({ workouts, sets });
    
    if (mlStatus.isActive) {
      //debugLog('✅ ML attivo e pronto all\'uso');
      //debugLog(`📊 Qualità dati: ${mlStatus.dataQuality}`);
    } else {
      //debugLog('⚠️ ML non attivo - dati insufficienti');
      //debugLog(`📉 Workouts: ${mlStatus.workoutsCount}/${mlStatus.requiredWorkouts}`);
      //debugLog(`📉 Sets: ${mlStatus.setsCount}/${mlStatus.requiredSets}`);
    }
  }
  
  // Non renderizza nulla, è solo un hook di diagnostica
  return null;
};

export default MLDebugger;